import { Component } from '@theme/component';

/**
 * @typedef {Object} CountdownTimerRefs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 */

/**
 * A custom element that counts down to a fixed end date (the same for every visitor) and
 * optionally hides itself once the countdown reaches zero.
 *
 * @extends {Component<CountdownTimerRefs>}
 */
class CountdownTimer extends Component {
  /** @type {number | undefined} */
  #intervalId;

  /** @type {number} */
  #endTime = 0;

  connectedCallback() {
    super.connectedCallback();

    const endDate = new Date(this.dataset.endDate ?? '');

    if (isNaN(endDate.getTime())) {
      this.hidden = true;
      return;
    }

    this.#endTime = endDate.getTime();
    this.#tick();
    this.#intervalId = window.setInterval(this.#tick, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#intervalId) window.clearInterval(this.#intervalId);
  }

  #tick = () => {
    const remaining = this.#endTime - Date.now();

    if (remaining <= 0) {
      if (this.#intervalId) window.clearInterval(this.#intervalId);

      if (this.dataset.hideOnExpire === 'true') {
        this.hidden = true;
      } else {
        this.#render(0, 0, 0, 0);
      }
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#render(days, hours, minutes, seconds);
  };

  /**
   * @param {number} days
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   */
  #render(days, hours, minutes, seconds) {
    const { days: daysRef, hours: hoursRef, minutes: minutesRef, seconds: secondsRef } = this.refs;
    if (daysRef) daysRef.textContent = String(days).padStart(2, '0');
    if (hoursRef) hoursRef.textContent = String(hours).padStart(2, '0');
    if (minutesRef) minutesRef.textContent = String(minutes).padStart(2, '0');
    if (secondsRef) secondsRef.textContent = String(seconds).padStart(2, '0');
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
